import "i18n/config";

import { AppFailedQueryReset } from "app/AppFailedQueryReset";
import { AppLoadingIndicator } from "app/AppLoadingIndicator";
import { AppLoggedOutLayout } from "app/AppLoggedOutLayout";
import { AppMaintenance } from "app/AppMaintenance";
import { AppRootErrorPage } from "app/AppRootErrorPage";
import { AppUpdatePrompt } from "app/AppUpdatePrompt";
import { useVersionInfoRetrieve } from "gen/endpoints/version/version";
import React from "react";
import { Outlet } from "react-router-dom";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { LoadingState } from "shared/components/empty/LoadingState";
import Page from "shared/components/layout/Page";
import { ConfirmationDialogProvider } from "shared/components/modal/confirm/ConfirmationDialogContext";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";

const AppRootLoading: React.FC = () => (
  <Page>
    <AppLoggedOutLayout>
      <LoadingState />
    </AppLoggedOutLayout>
  </Page>
);

const AppRootContent: React.FC = () => {
  const { data, isLoading } = useVersionInfoRetrieve({
    query: {
      throwOnError: false,
      // Keep session alive. axios-jwt will automatically refresh the token if it's expired
      refetchInterval: 1000 * 60 * 5, // 5 minutes
    },
  });
  if (isLoading) {
    return <AppRootLoading />;
  }
  if (data?.is_maintenance_mode) {
    return <AppMaintenance />;
  }
  return (
    <Page>
      <AppUpdatePrompt />
      <AppLoadingIndicator />
      <Outlet />
    </Page>
  );
};

const AppRoot: React.FC = () => (
  <QueryParamProvider
    adapter={ReactRouter6Adapter}
    options={{ removeDefaultsFromUrl: true, updateType: "replaceIn" }}
  >
    <QueryBoundary
      ErrorComponent={AppRootErrorPage}
      pendingFallback={<AppRootLoading />}
    >
      <ConfirmationDialogProvider>
        <AppRootContent />
      </ConfirmationDialogProvider>
      <AppFailedQueryReset />
    </QueryBoundary>
  </QueryParamProvider>
);

export default AppRoot;
